// Region-accurate currency for illustrative audit pricing (billed in USD).
import { useSyncExternalStore } from "react";

export const RATES = {
  USD: 1, EUR: 0.92, GBP: 0.79, INR: 87, AED: 3.67, CAD: 1.37, AUD: 1.52, SGD: 1.34,
  JPY: 150, CHF: 0.88, SAR: 3.75, ZAR: 18.2, NZD: 1.68,
};
export const CURRENCIES = Object.keys(RATES);

const REGION_CURRENCY = {
  US: "USD", GB: "GBP", IN: "INR", AE: "AED", CA: "CAD", AU: "AUD", SG: "SGD",
  JP: "JPY", CH: "CHF", LI: "CHF", SA: "SAR", ZA: "ZAR", NZ: "NZD",
};
const EURO_ZONE = ["DE", "FR", "IT", "ES", "NL", "IE", "PT", "BE", "AT", "FI", "GR", "LU", "SK", "SI", "EE", "LV", "LT", "CY", "MT", "HR"];

const TZ_COUNTRY = {
  "Asia/Kolkata": "IN", "Asia/Calcutta": "IN", "Asia/Dubai": "AE", "Asia/Riyadh": "SA", "Asia/Singapore": "SG",
  "Asia/Tokyo": "JP", "Europe/London": "GB", "Europe/Zurich": "CH", "Africa/Johannesburg": "ZA",
  "Pacific/Auckland": "NZ", "Europe/Berlin": "DE", "Europe/Paris": "FR", "Europe/Rome": "IT", "Europe/Madrid": "ES",
  "Europe/Amsterdam": "NL", "Europe/Dublin": "IE", "Europe/Lisbon": "PT", "Europe/Brussels": "BE", "Europe/Vienna": "AT",
  "Europe/Helsinki": "FI", "Europe/Athens": "GR", "Europe/Luxembourg": "LU", "Europe/Bratislava": "SK",
  "Europe/Ljubljana": "SI", "Europe/Tallinn": "EE", "Europe/Riga": "LV", "Europe/Vilnius": "LT", "Asia/Nicosia": "CY",
  "Europe/Malta": "MT", "Europe/Zagreb": "HR",
};
const TZ_PREFIX = [["Australia/", "AU"], ["America/Toronto", "CA"], ["America/Vancouver", "CA"], ["America/Edmonton", "CA"],
  ["America/Winnipeg", "CA"], ["America/Halifax", "CA"], ["America/St_Johns", "CA"], ["America/Regina", "CA"],
  ["America/New_York", "US"], ["America/Chicago", "US"], ["America/Denver", "US"], ["America/Los_Angeles", "US"],
  ["America/Phoenix", "US"], ["America/Anchorage", "US"], ["Pacific/Honolulu", "US"]];

const STORAGE_KEY = "tdm_currency";

export const currencyForCountry = (cc) =>
  !cc ? null : REGION_CURRENCY[cc] || (EURO_ZONE.includes(cc) ? "EUR" : "USD");

function countryFromTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    return TZ_COUNTRY[tz] || (TZ_PREFIX.find(([p]) => tz.startsWith(p)) || [])[1] || null;
  } catch (e) {
    return null;
  }
}

function countryFromLanguage() {
  try {
    const langs = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
    for (const l of langs) {
      const region = l.split("-")[1];
      if (region && region.length === 2) return region.toUpperCase();
    }
  } catch (e) {
    return null;
  }
  return null;
}

function readSaved() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return RATES[v] ? v : null;
  } catch (e) {
    return null;
  }
}

function initialState() {
  const saved = readSaved();
  if (saved) return { currency: saved, source: "manual", country: null };
  const tz = countryFromTimezone();
  if (tz) return { currency: currencyForCountry(tz), source: "timezone", country: tz };
  const lang = countryFromLanguage();
  if (lang) return { currency: currencyForCountry(lang), source: "language", country: lang };
  return { currency: "USD", source: "default", country: null };
}

let state = typeof window === "undefined" ? { currency: "USD", source: "default", country: null } : initialState();
const listeners = new Set();
const emit = () => listeners.forEach((l) => l());
const subscribe = (l) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

let lookupStarted = false;
function startLookup() {
  if (lookupStarted || state.source === "manual") return;
  lookupStarted = true;
  fetch("https://api.country.is/")
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => {
      const cc = d && typeof d.country === "string" ? d.country.toUpperCase() : null;
      if (!cc || state.source === "manual") return;
      state = { currency: currencyForCountry(cc), source: "location", country: cc };
      emit();
    })
    .catch(() => {});
}

export function setCurrency(cur) {
  if (!RATES[cur]) return;
  try {
    localStorage.setItem(STORAGE_KEY, cur);
  } catch (e) {
    // storage unavailable — keep in memory only
  }
  state = { ...state, currency: cur, source: "manual" };
  emit();
}

export function useCurrency() {
  startLookup();
  return useSyncExternalStore(subscribe, () => state, () => state);
}

export function detectCurrency() {
  return state.currency;
}

export function currencySymbol(cur) {
  try {
    const part = new Intl.NumberFormat("en", { style: "currency", currency: cur, currencyDisplay: "narrowSymbol" })
      .formatToParts(0)
      .find((p) => p.type === "currency");
    return part ? part.value : cur;
  } catch (e) {
    return cur;
  }
}

export function countryName(cc) {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(cc);
  } catch (e) {
    return cc;
  }
}

export function formatLocalPrice(usdAmount, currency) {
  const cur = currency || state.currency;
  const converted = Math.round(usdAmount * (RATES[cur] ?? 1));
  const display = ["CAD", "AUD", "SGD", "NZD"].includes(cur) ? "symbol" : "narrowSymbol";
  const opts = { style: "currency", currency: cur, currencyDisplay: display, maximumFractionDigits: 0, minimumFractionDigits: 0 };
  for (const loc of [CURRENCY_LOCALE[cur], "en"]) {
    try {
      return new Intl.NumberFormat(loc, opts).format(converted);
    } catch (e) {
      // try next locale
    }
  }
  return `${cur} ${converted}`;
}

const CURRENCY_LOCALE = {
  USD: "en-US", EUR: "en-IE", GBP: "en-GB", INR: "en-IN", AED: "en-US", CAD: "en-US", AUD: "en-US",
  SGD: "en-US", JPY: "en-JP", CHF: "de-CH", SAR: "en-US", ZAR: "en-US", NZD: "en-US",
};
